import * as THREE from "three";
import { LevelSim } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import { mat } from "./common";

/**
 * The Barracks' posts (TOWERS_V2, docs/TOWERS_V2.md "Barracks"). Each squad holds the road cell nearest its
 * Barracks (`sim.squads[i].post`); a small pennant in the Barracks' colours stands on the verge of that cell, on the
 * side of its tower, so the soldiers in the rank across the road read as that guardhouse's men, and not as the
 * fortress's troops (whose rally flag is the tall standard the player moves). It is set dressing only: it takes no
 * taps, and the fortress's rally flag and troop path dots (views/troops.ts) know nothing of it.
 */

/** Where the pennant stands: this far from the post cell's centre toward its Barracks (cells), and its height (m). */
export const PENNANT = { offset: 0.42, pole: 0.5, cloth: [0.2, 0.13] as const, wave: 7, cloth0: 0x9a2a26, trim: 0xe0b040, pole0: 0x4a3324 } as const;

/** The pennant's spot for a squad posted on `post` by a Barracks on `tower`: on the post's verge toward the tower. Pure. */
export function pennantSpot(post: { x: number; y: number }, tower: { x: number; y: number }): { x: number; z: number; yaw: number } {
  const dx = tower.x - post.x, dz = tower.y - post.y, d = Math.hypot(dx, dz);
  const k = d > 1e-6 ? PENNANT.offset / d : 0;
  return { x: post.x + dx * k, z: post.y + dz * k, yaw: Math.atan2(dx, dz) + Math.PI / 2 };
}

interface Pennant { root: THREE.Group; cloth: THREE.Mesh; base: Float32Array; seen: boolean; seed: number; key: string }

export class BarracksPosts {
  readonly group = new THREE.Group();
  private readonly pennants = new Map<number, Pennant>();
  private time = 0;
  private static readonly pole = new THREE.CylinderGeometry(0.012, 0.016, PENNANT.pole, 6).translate(0, PENNANT.pole / 2, 0);
  private static readonly knob = new THREE.SphereGeometry(0.022, 8, 6).translate(0, PENNANT.pole + 0.01, 0);

  constructor(private readonly sim: LevelSim, private readonly reduced: () => boolean) {}

  onTick(): void {
    for (const p of this.pennants.values()) p.seen = false;
    for (const q of this.sim.squads) {
      const t = this.sim.findTower(q.towerId);
      if (!t || !q.post) continue;
      let p = this.pennants.get(q.towerId);
      if (!p) { p = this.make(); this.pennants.set(q.towerId, p); }
      p.seen = true;
      const key = `${q.post.x},${q.post.y},${t.cell.x},${t.cell.y}`;           // a moved Barracks (M55) turns its pennant too
      if (p.key !== key) {
        p.key = key;
        const s = pennantSpot(q.post, t.cell);
        p.root.position.set(s.x, GroundMesh.drawnAt(s.x, s.z), s.z);
        p.root.rotation.y = s.yaw;
      }
    }
    for (const [id, p] of this.pennants) if (!p.seen) { this.group.remove(p.root); p.cloth.geometry.dispose(); this.pennants.delete(id); }
  }

  update(dt: number): void {
    this.time += dt;
    const still = this.reduced();
    for (const p of this.pennants.values()) {
      const pos = p.cloth.geometry.getAttribute("position") as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const x = p.base[i * 3], along = x / PENNANT.cloth[0];               // 0 at the pole, 1 at the tip
        pos.setZ(i, still ? 0 : Math.sin(this.time * PENNANT.wave - along * 5 + p.seed) * 0.03 * along);
      }
      pos.needsUpdate = true;
    }
  }

  private make(): Pennant {
    const root = new THREE.Group();
    const pole = new THREE.Mesh(BarracksPosts.pole, mat(PENNANT.pole0));
    const knob = new THREE.Mesh(BarracksPosts.knob, mat(PENNANT.trim));
    // a swallow-tailed pennant: a strip whose tip is notched, its colour in vertex order (cloth, then a gold hoist)
    const [w, h] = PENNANT.cloth;
    const geo = new THREE.PlaneGeometry(w, h, 6, 2).translate(w / 2, PENNANT.pole - h / 2 - 0.01, 0);
    const pos = geo.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) if (pos.getX(i) > w * 0.99 && Math.abs(pos.getY(i) - (PENNANT.pole - h / 2 - 0.01)) < 1e-3) pos.setX(i, w * 0.72);
    const cloth = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: PENNANT.cloth0, side: THREE.DoubleSide }));
    const hoist = new THREE.Mesh(new THREE.BoxGeometry(0.018, h * 1.04, 0.018).translate(0.009, PENNANT.pole - h / 2 - 0.01, 0), mat(PENNANT.trim));
    for (const m of [pole, knob, cloth, hoist]) { m.castShadow = true; root.add(m); }
    this.group.add(root);
    return { root, cloth, base: Float32Array.from(pos.array as Float32Array), seen: true, seed: Math.random() * 6, key: "" };
  }

  dispose(): void {
    for (const p of this.pennants.values()) { p.cloth.geometry.dispose(); (p.cloth.material as THREE.Material).dispose(); }
    this.pennants.clear();
    this.group.clear();
  }
}
